<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { mdiMenu } from '@mdi/js'
import { scrollToSection } from '~/composables/use-scroll-reveal'
import router from '~/router'

const scrolled = ref(false)
const mobileOpen = ref(false)

function onScroll() {
  scrolled.value = window.scrollY > 24
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})

const navLinks = [
  { label: 'Korzyści', id: 'korzysci' },
  { label: 'Dofinansowania', id: 'dofinansowania' },
  { label: 'Jak działa', id: 'jak-dziala' },
  // { label: 'Opinie', id: 'opinie' },
  { label: 'FAQ', id: 'faq' },
]

function scrollWhenReady(id: string, attempt = 0) {
  if (document.getElementById(id)) {
    scrollToSection(id)
    return
  }
  if (attempt < 30) {
    setTimeout(scrollWhenReady, 50, id, attempt + 1)
  }
}

function goTo(id: string) {
  mobileOpen.value = false
  if (router.currentRoute.path === '/') {
    scrollToSection(id)
    return
  }
  router.push('/').then(() => scrollWhenReady(id))
}
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <header
    class="landing-nav"
    :class="{'landing-nav--scrolled': scrolled}"
  >
    <div class="landing-nav__inner">
      <router-link
        class="landing-nav__brand"
        to="/"
      >
        <img
          alt="Snail benefit"
          class="landing-nav__logo-img"
          height="40"
          src="/logo.png"
        >
      </router-link>

      <nav class="hidden landing-nav__links lg:flex">
        <button
          v-for="link in navLinks"
          :key="link.id"
          class="landing-nav__link"
          type="button"
          @click="goTo(link.id)"
        >
          {{ link.label }}
        </button>
      </nav>

      <div class="landing-nav__actions">
        <v-btn
          class="hidden landing-nav__login sm:inline-flex"
          depressed
          text
          to="/login"
        >
          Logowanie
        </v-btn>

        <v-btn
          class="landing-btn landing-btn--primary landing-nav__cta"
          color="primary"
          depressed
          to="/contact"
        >
          Umów konsultację
        </v-btn>

        <v-btn
          class="lg:hidden"
          icon
          @click="mobileOpen = !mobileOpen"
        >
          <v-icon>{{ mdiMenu }}</v-icon>
        </v-btn>
      </div>
    </div>

    <div
      v-if="mobileOpen"
      class="landing-nav__mobile lg:hidden"
    >
      <button
        v-for="link in navLinks"
        :key="link.id"
        class="landing-nav__mobile-link"
        type="button"
        @click="goTo(link.id)"
      >
        {{ link.label }}
      </button>

      <v-btn
        block
        class="mt-4"
        text
        to="/login"
      >
        Logowanie
      </v-btn>
    </div>
  </header>
</template>

<style scoped>
.landing-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 16px 0;
  background: var(--landing-background);
  transition: background 0.3s ease, box-shadow 0.3s ease, padding 0.3s ease;
}

.landing-nav--scrolled {
  background: color-mix(in srgb, var(--landing-bg) 92%, transparent);
  backdrop-filter: blur(12px);
  box-shadow: 0 1px 0 var(--landing-surface);
  padding: 10px 0;
}

.landing-nav__inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.landing-nav__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 1.05rem;
  color: var(--landing-neutral);
  text-decoration: none;
}

.landing-nav__logo-img {
  flex-shrink: 0;
}

.landing-nav__links {
  gap: 8px;
}

.landing-nav__link {
  background: none;
  border: none;
  padding: 8px 14px;
  font-size: 0.9rem;
  color: var(--landing-secondary);
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.2s, background 0.2s;
}

.landing-nav__link:hover {
  color: var(--landing-neutral);
  background: var(--landing-surface);
}

.landing-nav__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.landing-nav__login {
  text-transform: none !important;
  color: var(--landing-secondary) !important;
}

.landing-nav__cta {
  text-transform: none !important;
}

.landing-nav__mobile {
  max-width: 1200px;
  margin: 8px auto 0;
  padding: 16px 20px 24px;
  background: var(--landing-bg);
  border-radius: 16px;
  box-shadow: 0 12px 40px color-mix(in srgb, var(--landing-dark) 12%, transparent);
}

.landing-nav__mobile-link {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 12px 0;
  font-size: 1rem;
  color: var(--landing-neutral);
  border-bottom: 1px solid var(--landing-surface);
  cursor: pointer;
}
</style>
